/**
 * Site Refresh 2026: product main section (Build-Spec 8b).
 * Loaded by snippets/refresh-product-main.liquid only.
 *
 * The theme's ProductForm (section-product.build.min.js) keeps doing the
 * variant work: it listens for trusted "change" events on the option radios,
 * re-renders .js-product-options through the Section Rendering API, updates the
 * price, the hidden variant id, the Add to cart state and ?variant= in the URL.
 * This file only adds the new controls on top of that.
 */
(() => {
	const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	function onThemeReady(callback) {
		if (window.themeCore && window.themeCore.loaded && window.themeCore.EventBus) {
			callback();
			return;
		}
		document.addEventListener('theme:all:loaded', callback, { once: true });
	}

	/* ---------- Gallery ---------- */

	function initGallery(section) {
		const gallery = section.querySelector('[data-rp-gallery]');
		if (!gallery) return;

		const track = gallery.querySelector('[data-rp-track]');
		const slides = [...gallery.querySelectorAll('[data-rp-slide]')];
		const thumbs = [...gallery.querySelectorAll('[data-rp-thumb]')];
		const count = gallery.querySelector('[data-rp-count]');
		const stage = gallery.querySelector('.rp-gallery__stage');
		const lightbox = gallery.querySelector('[data-rp-lightbox]');
		const lightboxImg = gallery.querySelector('[data-rp-lightbox-img]');
		let index = Number(gallery.dataset.start) || 0;
		let lightboxIndex = index;

		function setActive(next) {
			index = next;
			if (count) count.textContent = `${index + 1} / ${slides.length}`;
			thumbs.forEach((thumb, i) => {
				if (i === index) thumb.setAttribute('aria-current', 'true');
				else thumb.removeAttribute('aria-current');
			});
			slides.forEach((slide, i) => {
				slide.inert = i !== index;
			});
			const thumb = thumbs[index];
			if (thumb && thumb.parentElement) {
				const row = thumb.parentElement;
				const left = thumb.offsetLeft - row.offsetLeft;
				if (left < row.scrollLeft || left + thumb.offsetWidth > row.scrollLeft + row.clientWidth) {
					row.scrollTo({ left: left - 6, behavior: reducedMotion() ? 'auto' : 'smooth' });
				}
			}
		}

		function goTo(next, instant) {
			if (!slides.length) return;
			const target = (next + slides.length) % slides.length;
			track.scrollTo({ left: target * track.clientWidth, behavior: instant || reducedMotion() ? 'auto' : 'smooth' });
			setActive(target);
		}

		let scrollFrame = 0;
		track.addEventListener('scroll', () => {
			cancelAnimationFrame(scrollFrame);
			scrollFrame = requestAnimationFrame(() => {
				const width = track.clientWidth || 1;
				const current = Math.round(track.scrollLeft / width);
				if (current !== index && current >= 0 && current < slides.length) setActive(current);
			});
		}, { passive: true });

		stage.querySelector('[data-rp-prev]')?.addEventListener('click', () => goTo(index - 1));
		stage.querySelector('[data-rp-next]')?.addEventListener('click', () => goTo(index + 1));
		thumbs.forEach((thumb) => {
			thumb.addEventListener('click', () => goTo(Number(thumb.dataset.rpThumb)));
		});

		// Keep the current photo in place when the stage changes width.
		if ('ResizeObserver' in window) {
			new ResizeObserver(() => {
				track.scrollTo({ left: index * track.clientWidth, behavior: 'auto' });
			}).observe(track);
		}

		// Videos: the poster sits in the slide; the player is in a <template>.
		gallery.addEventListener('click', (event) => {
			const play = event.target.closest('[data-rp-play]');
			if (!play) return;
			const slide = play.closest('[data-rp-slide]');
			const template = slide && slide.querySelector('template[data-rp-video]');
			if (!template) return;
			slide.appendChild(template.content.cloneNode(true));
			play.remove();
			const player = slide.querySelector('.rp-gallery__video');
			if (player && player.tagName === 'IFRAME') {
				try {
					const url = new URL(player.src, window.location.href);
					url.searchParams.set('autoplay', '1');
					player.src = url.toString();
				} catch (e) { /* keep the original src */ }
			}
			if (player) player.focus();
		});

		// Full screen viewer: the photo sits on a dark backdrop with a filmstrip below it.
		// Tap / click (or + / -) zooms in on the spot, drag pans, the wheel and a two-finger
		// pinch zoom, a swipe changes photo, and tapping the backdrop or Esc closes.
		const MAX_SCALE = 3;
		const TAP_SCALE = 2.5;
		const lbStage = lightbox && lightbox.querySelector('[data-rp-lb-stage]');
		const lbCount = lightbox && lightbox.querySelector('[data-rp-lb-count]');
		const lbThumbs = lightbox ? [...lightbox.querySelectorAll('[data-rp-lb-thumb]')] : [];
		const lbZoom = lightbox && lightbox.querySelector('[data-rp-lb-zoom]');
		const pointers = new Map();
		let scale = 1;
		let tx = 0;
		let ty = 0;
		let gesture = null;

		const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

		// The size of the picture as drawn (object-fit: contain inside the stage box).
		function picture() {
			const boxW = lightboxImg.clientWidth;
			const boxH = lightboxImg.clientHeight;
			const fit = Math.min(boxW / (lightboxImg.naturalWidth || boxW), boxH / (lightboxImg.naturalHeight || boxH));
			return {
				boxW,
				boxH,
				width: (lightboxImg.naturalWidth || boxW) * fit,
				height: (lightboxImg.naturalHeight || boxH) * fit
			};
		}

		function render(animate) {
			lightboxImg.style.transition = animate && !reducedMotion() ? 'transform 0.25s ease' : 'none';
			lightboxImg.style.transform = scale === 1 ? '' : `translate(${tx}px, ${ty}px) scale(${scale})`;
			lightbox.classList.toggle('is-zoomed', scale > 1);
			if (lbZoom) {
				lbZoom.setAttribute('aria-label', scale > 1 ? lbZoom.dataset.labelOut : lbZoom.dataset.labelIn);
			}
		}

		// Zoom to `next`, keeping the point under (clientX, clientY) where it is.
		function zoomTo(next, clientX, clientY, animate) {
			next = clamp(next, 1, MAX_SCALE);
			const rect = lightboxImg.getBoundingClientRect();
			const px = clientX - (rect.left + rect.width / 2 - tx);
			const py = clientY - (rect.top + rect.height / 2 - ty);
			const k = next / scale;
			tx = px - k * (px - tx);
			ty = py - k * (py - ty);
			scale = next;
			panBy(0, 0);
			render(animate);
		}

		function panBy(dx, dy) {
			if (scale === 1) {
				tx = 0;
				ty = 0;
				return;
			}
			const pic = picture();
			const maxX = Math.max(0, (pic.width * scale - pic.boxW) / 2);
			const maxY = Math.max(0, (pic.height * scale - pic.boxH) / 2);
			tx = clamp(tx + dx, -maxX, maxX);
			ty = clamp(ty + dy, -maxY, maxY);
		}

		function resetZoom(animate) {
			scale = 1;
			tx = 0;
			ty = 0;
			render(animate);
		}

		function showInLightbox(i) {
			lightboxIndex = (i + slides.length) % slides.length;
			const shown = lightboxIndex;
			const slide = slides[shown];
			const full = slide.dataset.full || '';
			const stageImg = slide.querySelector('.rp-gallery__img');
			// The picture already on the page appears at once, then the large one replaces it.
			const instant = stageImg && stageImg.complete && stageImg.naturalWidth ? stageImg.currentSrc || stageImg.src : '';
			resetZoom(false);
			lightboxImg.alt = slide.dataset.alt || '';
			lightboxImg.src = instant || full;
			if (instant && full && full !== instant) {
				const probe = new Image();
				probe.onload = () => {
					if (lightboxIndex === shown) lightboxImg.src = full;
				};
				probe.src = full;
			}
			if (lbCount) lbCount.textContent = `${shown + 1} / ${slides.length}`;
			lbThumbs.forEach((thumb, n) => {
				if (n === shown) thumb.setAttribute('aria-current', 'true');
				else thumb.removeAttribute('aria-current');
			});
			const thumb = lbThumbs[shown];
			if (thumb) thumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
			// Warm up the neighbours so arrows and swipes feel instant.
			[shown - 1, shown + 1].forEach((n) => {
				const next = slides[(n + slides.length) % slides.length];
				if (next && next.dataset.full) new Image().src = next.dataset.full;
			});
		}

		stage.querySelector('[data-rp-full]')?.addEventListener('click', () => {
			if (!lightbox || typeof lightbox.showModal !== 'function') return;
			showInLightbox(index);
			lightbox.showModal();
			document.documentElement.classList.add('rp-lightbox-open');
		});

		if (lightbox && lbStage) {
			lightbox.querySelector('[data-rp-close]')?.addEventListener('click', () => lightbox.close());
			lightbox.querySelector('[data-rp-lb-prev]')?.addEventListener('click', () => showInLightbox(lightboxIndex - 1));
			lightbox.querySelector('[data-rp-lb-next]')?.addEventListener('click', () => showInLightbox(lightboxIndex + 1));
			lbThumbs.forEach((thumb, n) => thumb.addEventListener('click', () => showInLightbox(n)));
			lbZoom?.addEventListener('click', () => {
				const rect = lbStage.getBoundingClientRect();
				if (scale > 1) resetZoom(true);
				else zoomTo(TAP_SCALE, rect.left + rect.width / 2, rect.top + rect.height / 2, true);
			});

			lightbox.addEventListener('keydown', (event) => {
				if (event.key === 'ArrowLeft') showInLightbox(lightboxIndex - 1);
				else if (event.key === 'ArrowRight') showInLightbox(lightboxIndex + 1);
				else if (event.key === '+' || event.key === '=') zoomTo(scale * 1.5, window.innerWidth / 2, window.innerHeight / 2, true);
				else if (event.key === '-') zoomTo(scale / 1.5, window.innerWidth / 2, window.innerHeight / 2, true);
				else if (event.key === '0') resetZoom(true);
			});

			// Esc steps back out of a zoom first, then closes.
			lightbox.addEventListener('cancel', (event) => {
				if (scale > 1) {
					event.preventDefault();
					resetZoom(true);
				}
			});

			lbStage.addEventListener('wheel', (event) => {
				event.preventDefault();
				zoomTo(scale * Math.exp(-event.deltaY * 0.0025), event.clientX, event.clientY, false);
			}, { passive: false });

			// One pointer: tap zooms (or closes on the backdrop), drag pans, a swipe changes photo.
			// Two pointers: pinch.
			lbStage.addEventListener('pointerdown', (event) => {
				if (event.target.closest('button') || (event.pointerType === 'mouse' && event.button !== 0)) return;
				try {
					lbStage.setPointerCapture(event.pointerId);
				} catch (e) { /* the pointer already ended */ }
				pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
				if (pointers.size === 1) {
					gesture = { x: event.clientX, y: event.clientY, time: Date.now(), moved: false, pinched: false };
				} else if (pointers.size === 2 && gesture) {
					const [a, b] = [...pointers.values()];
					gesture.pinched = true;
					gesture.distance = Math.hypot(a.x - b.x, a.y - b.y) || 1;
					gesture.scale = scale;
				}
			});

			lbStage.addEventListener('pointermove', (event) => {
				const previous = pointers.get(event.pointerId);
				if (!previous || !gesture) return;
				const point = { x: event.clientX, y: event.clientY };
				pointers.set(event.pointerId, point);
				if (pointers.size === 2) {
					const [a, b] = [...pointers.values()];
					zoomTo(gesture.scale * (Math.hypot(a.x - b.x, a.y - b.y) / gesture.distance), (a.x + b.x) / 2, (a.y + b.y) / 2, false);
					gesture.moved = true;
					return;
				}
				if (Math.hypot(point.x - gesture.x, point.y - gesture.y) > 6) {
					gesture.moved = true;
					lightbox.classList.add('is-dragging');
				}
				if (scale > 1) {
					panBy(point.x - previous.x, point.y - previous.y);
					render(false);
				}
			});

			const endPointer = (event) => {
				if (!pointers.has(event.pointerId)) return;
				pointers.delete(event.pointerId);
				if (lbStage.hasPointerCapture(event.pointerId)) lbStage.releasePointerCapture(event.pointerId);
				if (pointers.size > 0 || !gesture) return;
				lightbox.classList.remove('is-dragging');
				const done = gesture;
				gesture = null;
				if (event.type === 'pointercancel' || done.pinched) return;
				const dx = event.clientX - done.x;
				const dy = event.clientY - done.y;
				if (!done.moved && Date.now() - done.time < 500) {
					const pic = picture();
					const rect = lightboxImg.getBoundingClientRect();
					const insidePicture = Math.abs(event.clientX - (rect.left + rect.width / 2)) <= (pic.width * scale) / 2
						&& Math.abs(event.clientY - (rect.top + rect.height / 2)) <= (pic.height * scale) / 2;
					if (!insidePicture && scale === 1) lightbox.close();
					else if (scale > 1) resetZoom(true);
					else zoomTo(TAP_SCALE, event.clientX, event.clientY, true);
				} else if (scale === 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
					showInLightbox(lightboxIndex + (dx < 0 ? 1 : -1));
				} else if (scale === 1 && dy > 90 && dy > Math.abs(dx) * 1.5) {
					lightbox.close();
				}
			};
			lbStage.addEventListener('pointerup', endPointer);
			lbStage.addEventListener('pointercancel', endPointer);

			// The picture is drawn to the stage, so a new window size changes how far it can pan.
			window.addEventListener('resize', () => {
				if (!lightbox.open || scale === 1) return;
				panBy(0, 0);
				render(false);
			});

			lightbox.addEventListener('click', (event) => {
				if (event.target === lightbox) lightbox.close();
			});
			lightbox.addEventListener('close', () => {
				document.documentElement.classList.remove('rp-lightbox-open');
				lightbox.classList.remove('is-dragging');
				pointers.clear();
				gesture = null;
				resetZoom(false);
				if (lightboxIndex !== index) goTo(lightboxIndex, true);
				stage.querySelector('[data-rp-full]')?.focus();
			});
		}

		// A variant with its own image shows that image, as the theme does.
		onThemeReady(() => {
			const sectionId = section.dataset.sectionId;
			window.themeCore.EventBus.listen(`pdp:section-${sectionId}:change-variant`, (data) => {
				const variant = data && data.variant;
				const mediaId = variant && variant.featured_media && variant.featured_media.id;
				if (!mediaId || (data.firstInitialization && !new URLSearchParams(window.location.search).has('variant'))) return;
				const target = slides.findIndex((slide) => Number(slide.dataset.mediaId) === Number(mediaId));
				if (target > -1 && target !== index) goTo(target);
			});
		});

		setActive(index);
		if (index > 0) requestAnimationFrame(() => goTo(index, true));
	}

	/* ---------- Units (in / cm): displayed numbers only ---------- */

	let unit = 'in';
	try {
		if (window.localStorage.getItem('rp-unit') === 'cm') unit = 'cm';
	} catch (e) { /* storage blocked: stay in inches */ }

	const cmLabel = () => {
		const button = document.querySelector('[data-rp-unit="cm"]');
		return (button && button.textContent.trim()) || 'cm';
	};

	// "40" -> "102 cm"; "40 × 22" or 20"W x 20"H -> "102 × 56 cm" / "51 × 51 cm"
	function toCentimetres(text) {
		const numbers = text.match(/\d+(?:\.\d+)?/g);
		if (!numbers) return text;
		const cm = numbers.map((n) => String(Math.round(parseFloat(n) * 2.54)));
		if (cm.length === 1) return `${cm[0]} ${cmLabel()}`;
		if (cm.length === 2 && /[x×]/i.test(text)) return `${cm[0]} × ${cm[1]} ${cmLabel()}`;
		let i = 0;
		return `${text.replace(/(\d+(?:\.\d+)?)(?:\s*(?:"|″|”))?/g, () => cm[i++])} ${cmLabel()}`;
	}

	function applyUnits(root) {
		root.querySelectorAll('[data-rp-dim]').forEach((el) => {
			if (el.dataset.rpIn === undefined) el.dataset.rpIn = el.textContent;
			el.textContent = unit === 'cm' ? toCentimetres(el.dataset.rpIn) : el.dataset.rpIn;
		});
		root.querySelectorAll('[data-rp-unit]').forEach((button) => {
			button.setAttribute('aria-pressed', String(button.dataset.rpUnit === unit));
		});
		// The size view's accessible name follows the labels (SVG and 3D canvas).
		root.querySelectorAll('[data-rp-size]').forEach((view) => {
			const svg = view.querySelector('svg[data-rp-size-title]');
			if (!svg) return;
			const parts = [...view.querySelectorAll('.rp-size__label, .rp-size__side-text b')].map((el) => el.textContent.replace(/\s+/g, ' ').trim());
			const label = `${svg.dataset.rpSizeTitle}: ${parts.join(', ')}`;
			svg.setAttribute('aria-label', label);
			const canvas = view.querySelector('canvas.rp-size__canvas');
			if (canvas) canvas.setAttribute('aria-label', label);
		});
	}

	/* ---------- 3D size view (task A0): loaded when the panel nears the viewport ---------- */

	function initSize3d(section) {
		let module = null;
		let loading = false;
		let observer = null;
		const panel = () => section.querySelector('[data-rp-size][data-rp-scene]');
		let webgl = null;
		const hasWebGL = () => {
			if (webgl === null) {
				try {
					const c = document.createElement('canvas');
					const gl = c.getContext('webgl2') || c.getContext('webgl');
					const lose = gl && gl.getExtension('WEBGL_lose_context');
					if (lose) lose.loseContext();
					webgl = !!gl;
				} catch (e) {
					webgl = false;
				}
			}
			return webgl;
		};

		function load() {
			const current = panel();
			if (loading || !current) return;
			loading = true;
			import(current.dataset.rpScene)
				// Let the page breathe between evaluating three.js and building the scene.
				.then((loaded) => new Promise((resolve) => { setTimeout(() => resolve(loaded), 0); }))
				.then((loaded) => {
					module = loaded;
					return module.sync(section);
				})
				.then(() => {
					// The scene puts its canvas in the stage; none means it failed.
					if (!section.querySelector('canvas.rp-size__canvas')) section.setAttribute('data-rp-3d', 'off');
				})
				.catch(() => { section.setAttribute('data-rp-3d', 'off'); /* no module: the SVG stays */ });
		}

		// The drawing is covered ("Loading the 3D view", refresh-product.css) until the
		// first 3D frame; "off" (no WebGL, or 3D failed) shows the drawing instead.
		section.setAttribute('data-rp-3d', hasWebGL() ? 'on' : 'off');

		function watch() {
			if (loading || section.getAttribute('data-rp-3d') === 'off') return;
			const current = panel();
			if (!current) return;
			if (!('IntersectionObserver' in window)) {
				load();
				return;
			}
			if (observer) observer.disconnect();
			observer = new IntersectionObserver((entries) => {
				if (!entries.some((entry) => entry.isIntersecting)) return;
				observer.disconnect();
				load();
			}, { rootMargin: '150px 0px' });
			observer.observe(current);
		}

		watch();

		return {
			// After the theme re-renders the steps: move the scene into the new panel.
			rendered() {
				if (module) module.sync(section).catch(() => {});
				else watch();
			},
		};
	}

	/* ---------- Option steps ---------- */

	function initOptions(section) {
		const form = section.querySelector('form[data-js-product-form]');
		const options = form && form.querySelector('[data-rp-options]');
		if (!form || !options) return;

		let variants = [];
		try {
			variants = JSON.parse(form.querySelector('[data-js-product-json]').textContent).variants || [];
		} catch (e) { /* no product JSON: buttons still set the clicked radios */ }
		const optionCount = variants.length ? variants[0].options.length : 3;
		let pendingFocus = null;

		const radiosFor = (index) => [...form.querySelectorAll(`input[name="option${index + 1}"]`)];

		function currentSelection() {
			const selection = [];
			for (let i = 0; i < optionCount; i += 1) {
				const checked = radiosFor(i).find((radio) => radio.checked);
				selection.push(checked ? checked.value : null);
			}
			return selection;
		}

		const exists = (selection) => variants.some((variant) => variant.options.every((value, i) => value === selection[i]));

		// Keep the clicked value(s); if that combination is not a variant, move the
		// other options to the nearest one that is, so an impossible pair is never sent.
		function resolve(desired, locked) {
			if (!variants.length || exists(desired)) return desired;
			let best = null;
			let bestScore = -1;
			variants.forEach((variant) => {
				if (!locked.every((i) => variant.options[i] === desired[i])) return;
				let score = variant.available ? 1 : 0;
				variant.options.forEach((value, i) => {
					if (!locked.includes(i) && value === desired[i]) score += 2;
				});
				if (score > bestScore) {
					best = variant;
					bestScore = score;
				}
			});
			return best ? best.options.slice() : desired;
		}

		form.addEventListener('click', (event) => {
			const button = event.target.closest('[data-rp-set]');
			if (!button || button.disabled || !options.contains(button)) return;

			const targets = button.dataset.rpSet.split(',').map((id) => document.getElementById(id)).filter(Boolean);
			if (!targets.length) return;

			const current = currentSelection();
			const desired = current.slice();
			const locked = [];
			targets.forEach((radio) => {
				const index = Number(radio.name.replace('option', '')) - 1;
				desired[index] = radio.value;
				locked.push(index);
			});
			// A chip keeps the options before it (Shopify's availability order).
			if (targets.length === 1) {
				for (let i = 0; i < locked[0]; i += 1) locked.push(i);
			}

			const next = resolve(desired, locked);
			const changed = [];
			next.forEach((value, i) => {
				if (value === current[i]) return;
				const radio = radiosFor(i).find((input) => input.value === value);
				if (radio) changed.push(radio);
			});
			if (!changed.length) return;

			const step = button.closest('.rp-step');
			if (step) {
				step.querySelectorAll('[data-rp-set]').forEach((other) => {
					other.setAttribute('aria-pressed', String(other === button));
				});
			}
			pendingFocus = document.activeElement === button ? button.dataset.rpKey : null;
			options.classList.add('is-busy');

			// Check all but the last silently; clicking the last one fires the
			// trusted "change" the theme's ProductForm listens for.
			changed.slice(0, -1).forEach((radio) => {
				radio.checked = true;
			});
			changed[changed.length - 1].click();
		});

		section.addEventListener('click', (event) => {
			const button = event.target.closest('[data-rp-unit]');
			if (!button) return;
			unit = button.dataset.rpUnit === 'cm' ? 'cm' : 'in';
			try {
				window.localStorage.setItem('rp-unit', unit);
			} catch (e) { /* storage blocked */ }
			applyUnits(section);
		});

		// The size view fades in when it shows a new size; a colour change
		// re-renders the same drawing, which then stays still.
		const sizeKey = () => {
			const view = options.querySelector('[data-rp-size]');
			return view ? view.dataset.rpSizeKey : null;
		};
		let lastSizeKey = sizeKey();
		const size3d = initSize3d(section);

		// The theme swaps the inner HTML of .js-product-options after each change.
		new MutationObserver(() => {
			options.classList.remove('is-busy');
			const key = sizeKey();
			if (key === lastSizeKey) {
				options.querySelectorAll('.rp-size__stage').forEach((stage) => stage.classList.add('is-same'));
			}
			lastSizeKey = key;
			applyUnits(options);
			size3d.rendered();
			if (pendingFocus) {
				const again = [...options.querySelectorAll('[data-rp-key]')].find((el) => el.dataset.rpKey === pendingFocus);
				if (again) again.focus({ preventScroll: true });
				pendingFocus = null;
			}
			section.dispatchEvent(new CustomEvent('rp:options-rendered', { bubbles: false }));
		}).observe(options, { childList: true });

		applyUnits(section);
	}

	/* ---------- Buy card: summary line and price follow the chosen variant ---------- */

	function initBuyCard(section) {
		const options = section.querySelector('[data-rp-options]');
		if (!options) return;

		function sync() {
			const template = options.querySelector('template[data-rp-summary]');
			let state = {};
			try {
				state = JSON.parse(options.querySelector('script[data-rp-state]').textContent);
			} catch (e) { /* keep what the server rendered */ }

			if (template) {
				section.querySelectorAll('[data-rp-summary-out]').forEach((el) => {
					el.innerHTML = template.innerHTML;
				});
			}
			if (typeof state.price === 'string') {
				section.querySelectorAll('[data-rp-price-out]').forEach((el) => {
					el.textContent = state.price;
				});
				section.querySelectorAll('[data-rp-atc]').forEach((button) => {
					button.dataset.rpPrice = state.price;
				});
			}
			if (state.image) {
				section.querySelectorAll('[data-rp-thumb-out]').forEach((img) => {
					img.src = state.image;
				});
			}
			applyUnits(section);
		}

		section.addEventListener('rp:options-rendered', sync);
	}

	/* ---------- Finish step (task A11): required, no default ---------- */

	function initFinish(section) {
		const form = section.querySelector('form[data-js-product-form]');
		const step = form && form.querySelector('[data-rp-finish]');
		if (!step) return;

		const inputs = [...step.querySelectorAll('[data-rp-finish-input]')];
		const error = step.querySelector('[data-rp-finish-error]');
		const valueOut = step.querySelector('[data-rp-finish-value]');
		const payment = section.querySelector('.rp-payment');

		const checked = () => inputs.find((input) => input.checked) || null;
		const labelOf = (input) => {
			const name = input.closest('label').querySelector('.rp-finish__name');
			return name ? name.textContent.trim() : input.value;
		};

		function setError(on) {
			step.classList.toggle('is-error', on);
			if (error) error.hidden = !on;
			inputs.forEach((input) => {
				if (on) input.setAttribute('aria-invalid', 'true');
				else input.removeAttribute('aria-invalid');
			});
		}

		// The summary lines (buy card, phone bar) end with the chosen finish.
		function paintSummary() {
			const input = checked();
			section.querySelectorAll('[data-rp-summary-out]').forEach((out) => {
				out.querySelectorAll('[data-rp-finish-out]').forEach((el) => el.remove());
				if (!input) return;
				const span = document.createElement('span');
				span.dataset.rpFinishOut = '';
				span.textContent = `${out.textContent.trim() ? ' · ' : ''}${labelOf(input)}`;
				out.appendChild(span);
			});
		}

		// Buy with Shop Pay skips the form's Add to cart, so it stays off (inert) until a finish is picked.
		function syncPayment() {
			if (!payment) return;
			const off = !checked();
			payment.classList.toggle('is-off', off);
			payment.inert = off;
			if (off) payment.setAttribute('aria-disabled', 'true');
			else payment.removeAttribute('aria-disabled');
		}

		function update() {
			const input = checked();
			if (valueOut) valueOut.textContent = input ? labelOf(input) : '';
			if (input) setError(false);
			paintSummary();
			syncPayment();
		}

		function block(event) {
			event.preventDefault();
			event.stopImmediatePropagation();
			setError(true);
			step.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
			inputs[0].focus({ preventScroll: true });
		}

		// Capture phase on the document, so this runs before the theme's own
		// Add to cart handlers: without a finish, nothing is added.
		document.addEventListener('click', (event) => {
			const button = event.target.closest('button[type="submit"], input[type="submit"]');
			if (!button || button.form !== form || checked()) return;
			block(event);
		}, true);
		document.addEventListener('submit', (event) => {
			if (event.target !== form || checked()) return;
			block(event);
		}, true);

		step.addEventListener('change', update);
		section.addEventListener('rp:options-rendered', paintSummary);
		update();
	}

	/* ---------- Drawers ---------- */

	function initDrawers(section) {
		section.addEventListener('click', (event) => {
			const toggle = event.target.closest('[data-rp-drawer]');
			if (!toggle) return;
			const panel = document.getElementById(toggle.getAttribute('aria-controls'));
			if (!panel) return;
			const open = toggle.getAttribute('aria-expanded') !== 'true';
			toggle.setAttribute('aria-expanded', String(open));
			panel.hidden = !open;
		});
	}

	/* ---------- Phone Add to cart bar ---------- */

	function initStickyBar(section) {
		const bar = section.querySelector('[data-rp-sticky]');
		const mainButton = section.querySelector('[data-rp-atc]');
		const card = section.querySelector('.rp-buycard');
		if (!bar || !mainButton || !card) return;

		const phone = window.matchMedia('(max-width: 989px)');

		// Any open drawer or dialog (cart drawer, menus, search) hides the bar.
		const drawerOpen = () => Boolean(document.querySelector('.drawer.is-open, .drawer.is-active, [id^="CartDrawer"].is-open, [id^="CartDrawer"].is-active, dialog[open]'));

		// Shown once Add to cart has scrolled up out of view, and only while no
		// part of the buy card is on screen. Read on scroll (not an
		// IntersectionObserver) so a jump straight past the button still counts.
		function update() {
			const pastButton = mainButton.getBoundingClientRect().bottom < 0;
			const cardRect = card.getBoundingClientRect();
			const cardInView = cardRect.bottom > 0 && cardRect.top < window.innerHeight;
			const show = phone.matches && pastButton && !cardInView && !drawerOpen();
			document.documentElement.classList.toggle('rp-sticky-on', show);
			if (show && bar.hidden) {
				bar.hidden = false;
				requestAnimationFrame(() => requestAnimationFrame(() => bar.classList.add('is-shown')));
			} else if (!show && !bar.hidden) {
				bar.classList.remove('is-shown');
				bar.hidden = true;
			}
		}

		let frame = 0;
		const schedule = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(update);
		};
		window.addEventListener('scroll', schedule, { passive: true });
		window.addEventListener('resize', schedule);
		if (phone.addEventListener) phone.addEventListener('change', update);
		update();
		new MutationObserver(schedule).observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });
	}

	/* ---------- Boot ---------- */

	function init() {
		document.querySelectorAll('[data-rp]').forEach((section) => {
			if (section.dataset.rpReady) return;
			section.dataset.rpReady = 'true';
			initGallery(section);
			initBuyCard(section);
			initFinish(section);
			initOptions(section);
			initDrawers(section);
			initStickyBar(section);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init, { once: true });
	} else {
		init();
	}
})();
